import { useEffect, useRef, useState } from 'react';
import { fetchCustomFieldDefinitionsApi } from './customFieldDefinitionsApi';

// Per account (keyed lowercase), the definition ids a write on that account's records will be allowed to name.
// A null entry means the account was asked about and did not answer.
export type CustomFieldAcceptance = Map<string, Set<string> | null>;

export const EMPTY_ACCEPTANCE: CustomFieldAcceptance = new Map();

export const acceptedFor = (acceptance: CustomFieldAcceptance, accountId: string): Set<string> | null =>
  acceptance.get(accountId.toLowerCase()) ?? null;

// The definitions endpoint resolves the account's parent server-side and returns the pair's definitions, which is
// the same set the value upsert authorizes against — so asking it is the only way the client can learn the parent leg.
export const useCustomFieldAcceptance = (accountIds: string[], entityType: string): CustomFieldAcceptance => {
  const [acceptance, setAcceptance] = useState<CustomFieldAcceptance>(EMPTY_ACCEPTANCE);
  const asked = useRef<Set<string>>(new Set());
  const cachedFor = useRef(entityType);
  const generation = useRef(0);
  const requested = accountIds.join(',');

  useEffect(() => {
    // Answers are keyed by account alone, so a changed entity type would otherwise be read - and refused on - as this one's.
    if (cachedFor.current !== entityType) {
      cachedFor.current = entityType;
      generation.current += 1;
      asked.current = new Set();
      setAcceptance(EMPTY_ACCEPTANCE);
    }
    const mine = generation.current;

    accountIds.forEach((accountId) => {
      const key = `${entityType}:${accountId.toLowerCase()}`;
      if (asked.current.has(key)) return;
      asked.current.add(key);

      fetchCustomFieldDefinitionsApi(accountId, undefined, entityType).then(({ data }) => {
        // An unanswered lookup stays null: "could not tell" must never harden into "accepts nothing".
        if (generation.current !== mine) return;
        const ids = data ? new Set(data.map((definition) => definition.id)) : null;
        setAcceptance((previous) => new Map(previous).set(accountId.toLowerCase(), ids));
      });
    });
  }, [requested, entityType]);

  return acceptance;
};
